import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { BarChart3, TrendingUp, DollarSign, Users, Plane, Building2, Calendar, PieChart, MapPin, Eye, RefreshCw, Wallet, Clock, Download, FileSpreadsheet, FileText, AlertTriangle, CheckCircle, Timer } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const parseDateString = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr.includes('T') || /^\d{4}-\d{2}-\d{2}/.test(dateStr)) return new Date(dateStr);
  const parts = dateStr.split('/');
  if (parts.length === 3) return new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
  return new Date(dateStr);
};

const Statistics = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [flights, setFlights] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [services, setServices] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [agents, setAgents] = useState([]);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAgency = currentUser.role === 'agency';
  const isAdmin = currentUser.role === 'admin';

  const [detailDialog, setDetailDialog] = useState({ open: false, type: '', data: [] });

  // Agent Cost Statistics states (admin only)
  const [costStats, setCostStats] = useState(null);
  const [costStatsLoading, setCostStatsLoading] = useState(false);
  const [costFilterYear, setCostFilterYear] = useState(new Date().getFullYear().toString());
  const [costFilterMonth, setCostFilterMonth] = useState("all");
  const [costFilterAgent, setCostFilterAgent] = useState("all");

  // On-time stats
  const [ontimeStats, setOntimeStats] = useState(null);
  const [ontimeLoading, setOntimeLoading] = useState(false);

  // Export states
  const [exporting, setExporting] = useState({});

  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedAgency, setSelectedAgency] = useState(isAgency ? currentUser.agency_id || "all" : "all");

  const [stats, setStats] = useState({
    totalRevenue: 0, totalVatAmount: 0, totalFlights: 0, totalPassengers: 0,
    avgRevenuePerFlight: 0, topAgency: { name: 'N/A', revenue: 0 },
    topService: { name: 'N/A', count: 0 }, monthlyGrowth: 0,
    flightsByType: { departure: 0, arrival: 0 }, flightsByAirport: {},
    flightsByDestination: {}, airlineUsage: {}, revenueByAgency: {},
    serviceUsage: {}, monthlyRevenue: [], invoiceStats: { total: 0, invoiced: 0, pending: 0 }
  });

  const formatTime = (date) => date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const token = localStorage.getItem('token');
        const requests = [
          axios.get(`${API}/flights`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/agencies`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/services`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/invoices?include_historical=true`, { headers: { Authorization: `Bearer ${token}` } })
        ];
        if (isAdmin) {
          requests.push(axios.get(`${API}/agents`, { headers: { Authorization: `Bearer ${token}` } }));
        }
        const responses = await Promise.all(requests);
        setFlights(responses[0].data);
        setAgencies(responses[1].data);
        setServices(responses[2].data);
        setInvoices(responses[3].data);
        if (isAdmin && responses[4]) setAgents(responses[4].data);
        setLoading(false);
        setRefreshing(false);
        setLastRefresh(new Date());
      } catch (error) {
        toast.error('Failed to fetch data');
        setLoading(false);
        setRefreshing(false);
      }
    };
    fetchInitialData();
  }, [isAdmin]);

  // Fetch on-time stats
  useEffect(() => {
    const fetchOntimeStats = async () => {
      setOntimeLoading(true);
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams();
        if (selectedYear && selectedYear !== 'all') params.append('year', selectedYear);
        if (selectedMonth && selectedMonth !== 'all') params.append('month', selectedMonth);
        if (selectedAgency && selectedAgency !== 'all') params.append('agency_id', selectedAgency);
        const response = await axios.get(`${API}/admin/statistics/ontime?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOntimeStats(response.data);
      } catch (error) {
        console.error('Failed to fetch on-time stats:', error);
      }
      setOntimeLoading(false);
    };
    fetchOntimeStats();
  }, [selectedYear, selectedMonth, selectedAgency]);

  // Calculate stats
  useEffect(() => {
    const calculateStatsFromData = () => {
      let filteredFlights = flights.filter(flight => {
        const flightDate = parseDateString(flight.flight_date);
        if (!flightDate || isNaN(flightDate.getTime())) return false;
        if (selectedYear !== "all" && flightDate.getFullYear().toString() !== selectedYear) return false;
        if (selectedMonth !== "all" && (flightDate.getMonth() + 1).toString() !== selectedMonth) return false;
        if (selectedAgency !== "all" && flight.agency_id !== selectedAgency) return false;
        return true;
      });

      let filteredInvoices = invoices.filter(invoice => {
        const invoiceDate = parseDateString(invoice.invoice_date);
        if (isNaN(invoiceDate.getTime())) return false;
        if (selectedYear !== "all" && invoiceDate.getFullYear().toString() !== selectedYear) return false;
        if (selectedMonth !== "all" && (invoiceDate.getMonth() + 1).toString() !== selectedMonth) return false;
        if (selectedAgency !== "all" && invoice.agency_id !== selectedAgency) return false;
        return true;
      });

      const totalRevenue = filteredInvoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
      const totalVatAmount = filteredInvoices.reduce((sum, inv) => {
        const totalWithVat = inv.total_amount_with_vat || inv.total_amount || 0;
        return sum + (totalWithVat - (inv.total_amount || 0));
      }, 0);
      const totalPassengers = filteredFlights.reduce((sum, f) => sum + (f.passenger_count || 0), 0);
      const avgRevenuePerFlight = filteredFlights.length > 0 ? totalRevenue / filteredFlights.length : 0;
      const flightsByType = {
        departure: filteredFlights.filter(f => f.flight_type === 'departure').length,
        arrival: filteredFlights.filter(f => f.flight_type === 'arrival').length
      };

      const flightsByAirport = {};
      filteredFlights.forEach(f => { const a = f.departure_airport || 'Unknown'; flightsByAirport[a] = (flightsByAirport[a] || 0) + 1; });

      const revenueByAgency = {};
      filteredInvoices.forEach(inv => { const n = inv.agency_name || 'Unknown'; revenueByAgency[n] = (revenueByAgency[n] || 0) + (inv.total_amount || 0); });

      let topAgency = { name: 'N/A', revenue: 0 };
      Object.entries(revenueByAgency).forEach(([name, revenue]) => { if (revenue > topAgency.revenue) topAgency = { name, revenue }; });

      const serviceUsage = {};
      filteredFlights.forEach(f => { (f.services || []).forEach(s => { serviceUsage[s.service_name] = (serviceUsage[s.service_name] || 0) + 1; }); });

      let topService = { name: 'N/A', count: 0 };
      Object.entries(serviceUsage).forEach(([name, count]) => { if (count > topService.count) topService = { name, count }; });

      const airlineUsage = {};
      filteredFlights.forEach(f => { const a = f.airline || 'Unknown'; if (a !== 'Unknown') airlineUsage[a] = (airlineUsage[a] || 0) + 1; });

      const flightsByDestination = {};
      filteredFlights.forEach(f => { const d = f.arrival_airport || 'Unknown'; flightsByDestination[d] = (flightsByDestination[d] || 0) + 1; });

      const monthlyRevenue = [];
      for (let i = 11; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const monthInvoices = invoices.filter(inv => {
          const invDate = parseDateString(inv.invoice_date);
          return invDate.getFullYear() === year && invDate.getMonth() + 1 === month;
        });
        monthlyRevenue.push({ month: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }), revenue: monthInvoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0) });
      }

      const previousPeriodInvoices = invoices.filter(inv => {
        const invDate = parseDateString(inv.invoice_date);
        if (!invDate) return false;
        if (selectedMonth !== "all") {
          const prevMonth = parseInt(selectedMonth) - 1 || 12;
          const prevYear = prevMonth === 12 ? parseInt(selectedYear) - 1 : parseInt(selectedYear);
          return invDate.getFullYear() === prevYear && invDate.getMonth() + 1 === prevMonth;
        }
        return invDate.getFullYear() === parseInt(selectedYear) - 1;
      });
      const previousPeriodRevenue = previousPeriodInvoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
      const monthlyGrowth = previousPeriodRevenue > 0 ? ((totalRevenue - previousPeriodRevenue) / previousPeriodRevenue) * 100 : 0;

      const invoiced = filteredFlights.filter(f => f.invoiced).length;
      const invoiceStats = { total: filteredFlights.length, invoiced, pending: filteredFlights.length - invoiced };

      setStats({
        totalRevenue, totalVatAmount, totalFlights: filteredFlights.length, totalPassengers,
        avgRevenuePerFlight, topAgency, topService, monthlyGrowth,
        flightsByType, flightsByAirport, flightsByDestination, airlineUsage,
        revenueByAgency, serviceUsage, monthlyRevenue, invoiceStats
      });
    };
    calculateStatsFromData();
  }, [flights, invoices, selectedYear, selectedMonth, selectedAgency]);

  // Fetch cost statistics (admin)
  useEffect(() => {
    if (!isAdmin) return;
    const fetchCostStatsData = async () => {
      setCostStatsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams();
        if (costFilterYear && costFilterYear !== 'all') params.append('year', costFilterYear);
        if (costFilterMonth && costFilterMonth !== 'all') params.append('month', costFilterMonth);
        if (costFilterAgent && costFilterAgent !== 'all') params.append('agent_id', costFilterAgent);
        const response = await axios.get(`${API}/admin/statistics/costs?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCostStats(response.data);
      } catch (error) {
        console.error('Failed to fetch cost statistics:', error);
      }
      setCostStatsLoading(false);
    };
    fetchCostStatsData();
  }, [isAdmin, costFilterYear, costFilterMonth, costFilterAgent]);

  const handleRefresh = async () => {
    setRefreshing(true);
    toast.success('Refreshing statistics...');
    try {
      const token = localStorage.getItem('token');
      const requests = [
        axios.get(`${API}/flights`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agencies`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/services`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/invoices?include_historical=true`, { headers: { Authorization: `Bearer ${token}` } })
      ];
      if (isAdmin) {
        requests.push(axios.get(`${API}/agents`, { headers: { Authorization: `Bearer ${token}` } }));
      }
      const responses = await Promise.all(requests);
      setFlights(responses[0].data);
      setAgencies(responses[1].data);
      setServices(responses[2].data);
      setInvoices(responses[3].data);
      if (isAdmin && responses[4]) setAgents(responses[4].data);
      setLastRefresh(new Date());
      setRefreshing(false);
      toast.success('Statistics updated successfully!');
    } catch (error) {
      toast.error('Failed to refresh data');
      setRefreshing(false);
    }
  };

  const handleExport = async (format, includeFinancial) => {
    const key = `${format}_${includeFinancial}`;
    setExporting(prev => ({ ...prev, [key]: true }));
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('include_financial', includeFinancial.toString());
      if (selectedYear && selectedYear !== 'all') params.append('year', selectedYear);
      if (selectedMonth && selectedMonth !== 'all') params.append('month', selectedMonth);
      if (selectedAgency && selectedAgency !== 'all') params.append('agency_id', selectedAgency);

      const response = await axios.get(`${API}/admin/statistics/export/${format}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const label = includeFinancial ? 'financial' : 'operational';
      const filename = `statistics_${label}.${ext}`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported successfully!`);
    } catch (error) {
      toast.error(`Failed to export ${format}`);
    }
    setExporting(prev => ({ ...prev, [key]: false }));
  };

  if (loading) return <div style={{ color: '#B0B0B0' }}>Loading statistics...</div>;

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#FFFFFF' }} data-testid="statistics-title">Statistics & Analytics</h1>
          <p style={{ color: '#B0B0B0' }}>Comprehensive business insights and metrics</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-sm text-right" style={{ color: '#B0B0B0' }}>
            Last updated: {formatTime(lastRefresh)}
          </div>
          <Button onClick={handleRefresh} disabled={refreshing} style={{ background: '#3B82F6', color: '#FFFFFF' }} data-testid="refresh-stats-btn">
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6" style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label style={{ color: '#E8E8E8' }}>Year</Label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} data-testid="filter-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  <SelectItem value="all">All Years</SelectItem>
                  {(() => { const years = []; for (let i = currentDate.getFullYear() - 4; i <= currentDate.getFullYear() + 1; i++) years.push(<SelectItem key={i} value={i.toString()}>{i}</SelectItem>); return years; })()}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label style={{ color: '#E8E8E8' }}>Month</Label>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} data-testid="filter-month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  <SelectItem value="all">All Months</SelectItem>
                  {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((month, idx) => (
                    <SelectItem key={idx + 1} value={(idx + 1).toString()}>{month}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!isAgency && (
              <div>
                <Label style={{ color: '#E8E8E8' }}>Agency</Label>
                <Select value={selectedAgency} onValueChange={setSelectedAgency}>
                  <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} data-testid="filter-agency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                    <SelectItem value="all">All Agencies</SelectItem>
                    {agencies.filter(a => a.is_active !== false).map(agency => (
                      <SelectItem key={agency.id} value={agency.id}>{agency.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Export Buttons */}
      {isAdmin && (
        <Card className="mb-6" style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center text-base" style={{ color: '#FFFFFF' }}>
              <Download className="mr-2 h-5 w-5" style={{ color: '#D4AF37' }} />
              Export Statistics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Button
                onClick={() => handleExport('excel', true)}
                disabled={exporting['excel_true']}
                className="flex items-center justify-center gap-2"
                style={{ background: '#22C55E', color: '#FFFFFF' }}
                data-testid="export-excel-financial"
              >
                <FileSpreadsheet className="h-4 w-4" />
                {exporting['excel_true'] ? 'Exporting...' : 'Excel + Financial'}
              </Button>
              <Button
                onClick={() => handleExport('excel', false)}
                disabled={exporting['excel_false']}
                className="flex items-center justify-center gap-2"
                style={{ background: '#3B82F6', color: '#FFFFFF' }}
                data-testid="export-excel-operational"
              >
                <FileSpreadsheet className="h-4 w-4" />
                {exporting['excel_false'] ? 'Exporting...' : 'Excel Operational'}
              </Button>
              <Button
                onClick={() => handleExport('pdf', true)}
                disabled={exporting['pdf_true']}
                className="flex items-center justify-center gap-2"
                style={{ background: '#EF4444', color: '#FFFFFF' }}
                data-testid="export-pdf-financial"
              >
                <FileText className="h-4 w-4" />
                {exporting['pdf_true'] ? 'Exporting...' : 'PDF + Financial'}
              </Button>
              <Button
                onClick={() => handleExport('pdf', false)}
                disabled={exporting['pdf_false']}
                className="flex items-center justify-center gap-2"
                style={{ background: '#F97316', color: '#FFFFFF' }}
                data-testid="export-pdf-operational"
              >
                <FileText className="h-4 w-4" />
                {exporting['pdf_false'] ? 'Exporting...' : 'PDF Operational'}
              </Button>
            </div>
            <p className="text-xs mt-2" style={{ color: '#B0B0B0' }}>
              Financial exports include revenue, VAT, and invoice data. Operational exports contain flight data only.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Key Metrics */}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${isAgency ? 'lg:grid-cols-2' : 'lg:grid-cols-5'} gap-4 mb-6`}>
        {!isAgency && (
          <Card style={{ background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(212, 175, 55, 0.05) 100%)', borderColor: '#4A5A8A' }}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center" style={{ color: '#D4AF37' }}>
                <DollarSign className="mr-2 h-4 w-4" /> Total Revenue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" style={{ color: '#FFFFFF' }} data-testid="total-revenue">
                &euro;{stats.totalRevenue.toFixed(2)}
              </div>
              <p className="text-xs mt-1" style={{ color: stats.monthlyGrowth >= 0 ? '#22C55E' : '#EF4444' }}>
                {stats.monthlyGrowth >= 0 ? '+' : ''}{stats.monthlyGrowth.toFixed(1)}% vs previous period
              </p>
            </CardContent>
          </Card>
        )}
        {!isAgency && (
          <Card style={{ background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.15) 0%, rgba(249, 115, 22, 0.05) 100%)', borderColor: '#4A5A8A' }}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center" style={{ color: '#F97316' }}>
                <TrendingUp className="mr-2 h-4 w-4" /> VAT Amount
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>&euro;{stats.totalVatAmount.toFixed(2)}</div>
              <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>21% VAT on Belgian invoices</p>
            </CardContent>
          </Card>
        )}
        <Card style={{ background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.15) 0%, rgba(96, 165, 250, 0.05) 100%)', borderColor: '#4A5A8A' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center" style={{ color: '#60A5FA' }}>
              <Plane className="mr-2 h-4 w-4" /> Total Flights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ color: '#FFFFFF' }} data-testid="total-flights">{stats.totalFlights}</div>
            {!isAgency && <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>Avg &euro;{stats.avgRevenuePerFlight.toFixed(2)} per flight</p>}
            {isAgency && <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>Your flight operations</p>}
          </CardContent>
        </Card>
        <Card style={{ background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(34, 197, 94, 0.05) 100%)', borderColor: '#4A5A8A' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center" style={{ color: '#22C55E' }}>
              <Users className="mr-2 h-4 w-4" /> Total Passengers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ color: '#FFFFFF' }} data-testid="total-passengers">{stats.totalPassengers.toLocaleString()}</div>
            <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>Avg {stats.totalFlights > 0 ? (stats.totalPassengers / stats.totalFlights).toFixed(1) : 0} per flight</p>
          </CardContent>
        </Card>
        {!isAgency && (
          <Card style={{ background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.15) 0%, rgba(168, 85, 247, 0.05) 100%)', borderColor: '#4A5A8A' }}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center" style={{ color: '#A855F7' }}>
                <TrendingUp className="mr-2 h-4 w-4" /> Top Agency
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold" style={{ color: '#FFFFFF' }}>{stats.topAgency.name}</div>
              <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>&euro;{stats.topAgency.revenue.toFixed(2)} revenue</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* On-Time Departure Statistics */}
      <div className="mb-6">
        <Card style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <CardHeader>
            <CardTitle className="flex items-center" style={{ color: '#FFFFFF' }}>
              <Clock className="mr-2 h-5 w-5" style={{ color: '#D4AF37' }} />
              On-Time Departure Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {ontimeLoading ? (
              <div style={{ color: '#B0B0B0' }}>Loading on-time statistics...</div>
            ) : ontimeStats && ontimeStats.flights_with_data > 0 ? (
              <div>
                {/* OTP Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="p-3 rounded-lg" style={{ background: '#0A1128' }}>
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="h-4 w-4" style={{ color: '#22C55E' }} />
                      <span className="text-xs" style={{ color: '#B0B0B0' }}>On-Time Rate</span>
                    </div>
                    <div className="text-2xl font-bold" style={{ color: '#22C55E' }} data-testid="ontime-rate">
                      {ontimeStats.on_time_rate}%
                    </div>
                    <div className="text-xs" style={{ color: '#B0B0B0' }}>{ontimeStats.on_time} of {ontimeStats.flights_with_data} flights</div>
                  </div>
                  <div className="p-3 rounded-lg" style={{ background: '#0A1128' }}>
                    <div className="flex items-center gap-2 mb-1">
                      <Timer className="h-4 w-4" style={{ color: '#F97316' }} />
                      <span className="text-xs" style={{ color: '#B0B0B0' }}>Avg Delay</span>
                    </div>
                    <div className="text-2xl font-bold" style={{ color: '#F97316' }} data-testid="avg-delay">
                      {ontimeStats.avg_delay} min
                    </div>
                    <div className="text-xs" style={{ color: '#B0B0B0' }}>Max: {ontimeStats.max_delay} min</div>
                  </div>
                  <div className="p-3 rounded-lg" style={{ background: '#0A1128' }}>
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className="h-4 w-4" style={{ color: '#EF4444' }} />
                      <span className="text-xs" style={{ color: '#B0B0B0' }}>Delayed</span>
                    </div>
                    <div className="text-2xl font-bold" style={{ color: '#EF4444' }} data-testid="delayed-count">
                      {ontimeStats.delayed}
                    </div>
                    <div className="text-xs" style={{ color: '#B0B0B0' }}>More than 5 min late</div>
                  </div>
                  <div className="p-3 rounded-lg" style={{ background: '#0A1128' }}>
                    <div className="flex items-center gap-2 mb-1">
                      <Plane className="h-4 w-4" style={{ color: '#60A5FA' }} />
                      <span className="text-xs" style={{ color: '#B0B0B0' }}>Early</span>
                    </div>
                    <div className="text-2xl font-bold" style={{ color: '#60A5FA' }} data-testid="early-count">
                      {ontimeStats.early}
                    </div>
                    <div className="text-xs" style={{ color: '#B0B0B0' }}>More than 5 min early</div>
                  </div>
                </div>

                {/* Delay Distribution */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium mb-3" style={{ color: '#E8E8E8' }}>Delay Distribution</h3>
                    <div className="space-y-2">
                      {Object.entries(ontimeStats.delay_distribution).map(([range, count]) => {
                        const pct = (count / ontimeStats.flights_with_data) * 100;
                        const color = range === '0-5' ? '#22C55E' : range === '5-15' ? '#F97316' : range === '15-30' ? '#EF4444' : range === '30-60' ? '#DC2626' : '#991B1B';
                        return (
                          <div key={range}>
                            <div className="flex justify-between mb-1">
                              <span className="text-xs" style={{ color: '#E8E8E8' }}>{range} min</span>
                              <span className="text-xs font-medium" style={{ color }}>{count} ({pct.toFixed(1)}%)</span>
                            </div>
                            <div className="w-full bg-gray-700 rounded-full h-2">
                              <div className="h-2 rounded-full" style={{ width: `${pct}%`, background: color }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* By Agency */}
                  <div>
                    <h3 className="text-sm font-medium mb-3" style={{ color: '#E8E8E8' }}>On-Time Rate by Agency</h3>
                    <div className="space-y-2 max-h-48 overflow-auto">
                      {ontimeStats.by_agency.map(a => (
                        <div key={a.name} className="p-2 rounded" style={{ background: '#0A1128' }}>
                          <div className="flex justify-between text-xs mb-1">
                            <span style={{ color: '#E8E8E8' }}>{a.name.substring(0, 30)}</span>
                            <span style={{ color: a.rate >= 50 ? '#22C55E' : a.rate >= 25 ? '#F97316' : '#EF4444' }}>{a.rate}%</span>
                          </div>
                          <div className="flex justify-between text-xs" style={{ color: '#B0B0B0' }}>
                            <span>{a.total} flights</span>
                            <span>Avg delay: {a.avg_delay} min</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Top Delays Table */}
                {ontimeStats.records && ontimeStats.records.length > 0 && (
                  <div className="mt-4">
                    <h3 className="text-sm font-medium mb-3" style={{ color: '#E8E8E8' }}>Top Delays</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr style={{ borderBottom: '1px solid #4A5A8A' }}>
                            <th className="text-left py-2 px-2" style={{ color: '#B0B0B0' }}>Flight</th>
                            <th className="text-left py-2 px-2" style={{ color: '#B0B0B0' }}>Date</th>
                            <th className="text-left py-2 px-2" style={{ color: '#B0B0B0' }}>Agency</th>
                            <th className="text-left py-2 px-2" style={{ color: '#B0B0B0' }}>Scheduled</th>
                            <th className="text-left py-2 px-2" style={{ color: '#B0B0B0' }}>Actual</th>
                            <th className="text-right py-2 px-2" style={{ color: '#B0B0B0' }}>Delay</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ontimeStats.records.slice(0, 10).map((r, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #2A3A5A' }}>
                              <td className="py-1.5 px-2" style={{ color: '#E8E8E8' }}>{r.flight_number}</td>
                              <td className="py-1.5 px-2" style={{ color: '#B0B0B0' }}>{r.flight_date}</td>
                              <td className="py-1.5 px-2" style={{ color: '#B0B0B0' }}>{r.agency_name.substring(0, 25)}</td>
                              <td className="py-1.5 px-2" style={{ color: '#B0B0B0' }}>{r.scheduled}</td>
                              <td className="py-1.5 px-2" style={{ color: '#B0B0B0' }}>{r.actual}</td>
                              <td className="py-1.5 px-2 text-right font-medium" style={{ color: r.delay_minutes > 30 ? '#EF4444' : r.delay_minutes > 5 ? '#F97316' : '#22C55E' }}>
                                {r.delay_minutes > 0 ? '+' : ''}{r.delay_minutes} min
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8" style={{ color: '#B0B0B0' }}>
                <Clock className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No on-time departure data available for the selected period.</p>
                <p className="text-xs mt-1">Actual departure times are recorded in operational reports.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Invoice Status (admin only) */}
      {!isAgency && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card style={{ background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.12) 0%, rgba(34, 197, 94, 0.03) 100%)', borderColor: '#4A5A8A' }}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center" style={{ color: '#22C55E' }}>
                <CheckCircle className="mr-2 h-4 w-4" /> Invoiced Flights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" style={{ color: '#FFFFFF' }} data-testid="invoiced-flights">{stats.invoiceStats.invoiced}</div>
              <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                {stats.invoiceStats.total > 0 ? ((stats.invoiceStats.invoiced / stats.invoiceStats.total) * 100).toFixed(1) : 0}% of total flights
              </p>
            </CardContent>
          </Card>
          <Card style={{ background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.12) 0%, rgba(249, 115, 22, 0.03) 100%)', borderColor: '#4A5A8A' }}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center" style={{ color: '#F97316' }}>
                <AlertTriangle className="mr-2 h-4 w-4" /> Pending Invoicing
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" style={{ color: '#FFFFFF' }} data-testid="pending-flights">{stats.invoiceStats.pending}</div>
              <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                {stats.invoiceStats.total > 0 ? ((stats.invoiceStats.pending / stats.invoiceStats.total) * 100).toFixed(1) : 0}% not yet invoiced
              </p>
            </CardContent>
          </Card>
          <Card style={{ background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.12) 0%, rgba(96, 165, 250, 0.03) 100%)', borderColor: '#4A5A8A' }}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center" style={{ color: '#60A5FA' }}>
                <FileText className="mr-2 h-4 w-4" /> Total Invoices
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>{invoices.length}</div>
              <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>Across all periods</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts Row 1 */}
      <div className={`grid grid-cols-1 ${isAgency ? 'lg:grid-cols-1' : 'lg:grid-cols-2'} gap-6 mb-6`}>
        {!isAgency && (
          <Card
            style={{ background: '#1E2A4A', borderColor: '#4A5A8A', cursor: 'pointer' }}
            onClick={() => setDetailDialog({ open: true, type: 'revenue', data: Object.entries(stats.revenueByAgency).sort(([,a], [,b]) => b - a) })}
          >
            <CardHeader>
              <CardTitle className="flex items-center justify-between" style={{ color: '#FFFFFF' }}>
                <div className="flex items-center"><Building2 className="mr-2 h-5 w-5" /> Revenue by Agency</div>
                <Eye className="h-5 w-5" style={{ color: '#D4AF37' }} />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(stats.revenueByAgency).sort(([,a], [,b]) => b - a).slice(0, 10).map(([agency, revenue]) => {
                  const percentage = (revenue / stats.totalRevenue) * 100;
                  return (
                    <div key={agency}>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm" style={{ color: '#E8E8E8' }}>{agency}</span>
                        <span className="text-sm font-medium" style={{ color: '#D4AF37' }}>&euro;{revenue.toFixed(2)}</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div className="h-2 rounded-full" style={{ width: `${percentage}%`, background: '#D4AF37' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        <Card
          style={{ background: '#1E2A4A', borderColor: '#4A5A8A', cursor: 'pointer' }}
          onClick={() => setDetailDialog({ open: true, type: 'airports', data: Object.entries(stats.flightsByAirport).sort(([,a], [,b]) => b - a) })}
        >
          <CardHeader>
            <CardTitle className="flex items-center justify-between" style={{ color: '#FFFFFF' }}>
              <div className="flex items-center"><Plane className="mr-2 h-5 w-5" /> Flights by Airport</div>
              <Eye className="h-5 w-5" style={{ color: '#60A5FA' }} />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats.flightsByAirport).sort(([,a], [,b]) => b - a).slice(0, 10).map(([airport, count]) => {
                const percentage = (count / stats.totalFlights) * 100;
                return (
                  <div key={airport}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm" style={{ color: '#E8E8E8' }}>{airport}</span>
                      <span className="text-sm font-medium" style={{ color: '#60A5FA' }}>{count} flights</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div className="h-2 rounded-full" style={{ width: `${percentage}%`, background: '#60A5FA' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card
          style={{ background: '#1E2A4A', borderColor: '#4A5A8A', cursor: 'pointer' }}
          onClick={() => setDetailDialog({ open: true, type: 'services', data: Object.entries(stats.serviceUsage).sort(([,a], [,b]) => b - a) })}
        >
          <CardHeader>
            <CardTitle className="flex items-center justify-between" style={{ color: '#FFFFFF' }}>
              <div className="flex items-center"><BarChart3 className="mr-2 h-5 w-5" /> Most Used Services</div>
              <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setDetailDialog({ open: true, type: 'services', data: Object.entries(stats.serviceUsage).sort(([,a], [,b]) => b - a) }); }} style={{ borderColor: '#4A5A8A', color: '#22C55E' }}>
                <Eye className="h-4 w-4 mr-1" /> View All
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats.serviceUsage).sort(([,a], [,b]) => b - a).slice(0, 5).map(([service, count]) => {
                const maxCount = Math.max(...Object.values(stats.serviceUsage));
                const percentage = (count / maxCount) * 100;
                return (
                  <div key={service}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm" style={{ color: '#E8E8E8' }}>{service}</span>
                      <span className="text-sm font-medium" style={{ color: '#22C55E' }}>{count}x</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div className="h-2 rounded-full" style={{ width: `${percentage}%`, background: '#22C55E' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card
          style={{ background: '#1E2A4A', borderColor: '#4A5A8A', cursor: 'pointer' }}
          onClick={() => setDetailDialog({ open: true, type: 'airlines', data: Object.entries(stats.airlineUsage).sort(([,a], [,b]) => b - a) })}
        >
          <CardHeader>
            <CardTitle className="flex items-center justify-between" style={{ color: '#FFFFFF' }}>
              <div className="flex items-center"><Plane className="mr-2 h-5 w-5" /> Most Used Airlines</div>
              <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setDetailDialog({ open: true, type: 'airlines', data: Object.entries(stats.airlineUsage).sort(([,a], [,b]) => b - a) }); }} style={{ borderColor: '#4A5A8A', color: '#60A5FA' }}>
                <Eye className="h-4 w-4 mr-1" /> View All
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats.airlineUsage).sort(([,a], [,b]) => b - a).slice(0, 5).map(([airline, count]) => {
                const maxCount = Math.max(...Object.values(stats.airlineUsage));
                const percentage = (count / maxCount) * 100;
                return (
                  <div key={airline}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm" style={{ color: '#E8E8E8' }}>{airline}</span>
                      <span className="text-sm font-medium" style={{ color: '#60A5FA' }}>{count} flights</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div className="h-2 rounded-full" style={{ width: `${percentage}%`, background: '#60A5FA' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 3 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card
          style={{ background: '#1E2A4A', borderColor: '#4A5A8A', cursor: 'pointer' }}
          onClick={() => setDetailDialog({ open: true, type: 'destinations', data: Object.entries(stats.flightsByDestination).sort(([,a], [,b]) => b - a) })}
        >
          <CardHeader>
            <CardTitle className="flex items-center justify-between" style={{ color: '#FFFFFF' }}>
              <div className="flex items-center"><MapPin className="mr-2 h-5 w-5" /> Top Destinations</div>
              <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setDetailDialog({ open: true, type: 'destinations', data: Object.entries(stats.flightsByDestination).sort(([,a], [,b]) => b - a) }); }} style={{ borderColor: '#4A5A8A', color: '#A855F7' }}>
                <Eye className="h-4 w-4 mr-1" /> View All
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats.flightsByDestination).sort(([,a], [,b]) => b - a).slice(0, 5).map(([destination, count]) => {
                const maxCount = Math.max(...Object.values(stats.flightsByDestination));
                const percentage = (count / maxCount) * 100;
                return (
                  <div key={destination}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm" style={{ color: '#E8E8E8' }}>{destination}</span>
                      <span className="text-sm font-medium" style={{ color: '#A855F7' }}>{count} flights</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div className="h-2 rounded-full" style={{ width: `${percentage}%`, background: '#A855F7' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <CardHeader>
            <CardTitle className="flex items-center" style={{ color: '#FFFFFF' }}>
              <PieChart className="mr-2 h-5 w-5" /> Flight Types Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm" style={{ color: '#E8E8E8' }}>Departures</span>
                  <span className="text-sm font-medium" style={{ color: '#60A5FA' }}>
                    {stats.flightsByType.departure} ({stats.totalFlights > 0 ? ((stats.flightsByType.departure / stats.totalFlights) * 100).toFixed(1) : 0}%)
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-3">
                  <div className="h-3 rounded-full" style={{ width: `${stats.totalFlights > 0 ? (stats.flightsByType.departure / stats.totalFlights) * 100 : 0}%`, background: '#60A5FA' }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm" style={{ color: '#E8E8E8' }}>Arrivals</span>
                  <span className="text-sm font-medium" style={{ color: '#A855F7' }}>
                    {stats.flightsByType.arrival} ({stats.totalFlights > 0 ? ((stats.flightsByType.arrival / stats.totalFlights) * 100).toFixed(1) : 0}%)
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-3">
                  <div className="h-3 rounded-full" style={{ width: `${stats.totalFlights > 0 ? (stats.flightsByType.arrival / stats.totalFlights) * 100 : 0}%`, background: '#A855F7' }} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Revenue Trend */}
      {!isAgency && (
        <Card className="mb-6" style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <CardHeader>
            <CardTitle className="flex items-center" style={{ color: '#FFFFFF' }}>
              <Calendar className="mr-2 h-5 w-5" /> Monthly Revenue Trend (Last 12 Months)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.monthlyRevenue.map((item, idx) => {
                const maxRevenue = Math.max(...stats.monthlyRevenue.map(m => m.revenue));
                const percentage = maxRevenue > 0 ? (item.revenue / maxRevenue) * 100 : 0;
                return (
                  <div key={idx}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm" style={{ color: '#E8E8E8' }}>{item.month}</span>
                      <span className="text-sm font-medium" style={{ color: '#D4AF37' }}>&euro;{item.revenue.toFixed(2)}</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div className="h-2 rounded-full" style={{ width: `${percentage}%`, background: '#D4AF37' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Agent Cost Statistics Section - Admin Only */}
      {isAdmin && (
        <div className="mt-8">
          <div className="mb-4">
            <h2 className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>Agent Cost Statistics</h2>
            <p style={{ color: '#B0B0B0' }}>Track agent payments and outstanding amounts</p>
          </div>

          <Card className="mb-6" style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Year</Label>
                  <Select value={costFilterYear} onValueChange={setCostFilterYear}>
                    <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                      <SelectItem value="all">All Years</SelectItem>
                      {(() => { const years = []; for (let i = currentDate.getFullYear() - 2; i <= currentDate.getFullYear() + 1; i++) years.push(<SelectItem key={i} value={i.toString()}>{i}</SelectItem>); return years; })()}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Month</Label>
                  <Select value={costFilterMonth} onValueChange={setCostFilterMonth}>
                    <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                      <SelectItem value="all">All Months</SelectItem>
                      {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((month, idx) => (
                        <SelectItem key={idx + 1} value={(idx + 1).toString()}>{month}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Agent</Label>
                  <Select value={costFilterAgent} onValueChange={setCostFilterAgent}>
                    <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                      <SelectItem value="all">All Agents</SelectItem>
                      {agents.map(agent => (
                        <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {costStats && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Card style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0.05) 100%)', borderColor: '#4A5A8A' }}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center" style={{ color: '#3B82F6' }}>
                      <Wallet className="mr-2 h-4 w-4" /> Total To Be Paid
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>&euro;{(costStats.totals?.total_to_be_paid || 0).toFixed(2)}</div>
                    <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>{costStats.totals?.total_flights || 0} flights</p>
                  </CardContent>
                </Card>
                <Card style={{ background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(34, 197, 94, 0.05) 100%)', borderColor: '#4A5A8A' }}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center" style={{ color: '#22C55E' }}>
                      <DollarSign className="mr-2 h-4 w-4" /> Total Paid
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>&euro;{(costStats.totals?.total_paid || 0).toFixed(2)}</div>
                    <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>{costStats.totals?.total_agents || 0} agents</p>
                  </CardContent>
                </Card>
                <Card style={{ background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(239, 68, 68, 0.05) 100%)', borderColor: '#4A5A8A' }}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center" style={{ color: '#EF4444' }}>
                      <TrendingUp className="mr-2 h-4 w-4" /> Outstanding
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>&euro;{(costStats.totals?.total_difference || 0).toFixed(2)}</div>
                    <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>Remaining to pay</p>
                  </CardContent>
                </Card>
                <Card style={{ background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.15) 0%, rgba(168, 85, 247, 0.05) 100%)', borderColor: '#4A5A8A' }}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center" style={{ color: '#A855F7' }}>
                      <Users className="mr-2 h-4 w-4" /> Payment Rate
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>
                      {costStats.totals?.total_to_be_paid > 0 ? ((costStats.totals?.total_paid / costStats.totals?.total_to_be_paid) * 100).toFixed(1) : 0}%
                    </div>
                    <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>Of total costs paid</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  <CardHeader>
                    <CardTitle className="flex items-center" style={{ color: '#FFFFFF' }}>
                      <Users className="mr-2 h-5 w-5" /> Cost by Agent
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {costStatsLoading ? (
                      <div style={{ color: '#B0B0B0' }}>Loading...</div>
                    ) : (
                      <div className="space-y-3 max-h-96 overflow-auto">
                        {(costStats.agents || []).map((agent, idx) => {
                          const maxCost = Math.max(...(costStats.agents || []).map(a => a.total_to_be_paid));
                          const percentage = maxCost > 0 ? (agent.total_to_be_paid / maxCost) * 100 : 0;
                          const paidPercentage = agent.total_to_be_paid > 0 ? (agent.total_paid / agent.total_to_be_paid) * 100 : 0;
                          return (
                            <div key={agent.agent_id} className="p-3 rounded" style={{ background: '#0A1128' }}>
                              <div className="flex justify-between mb-2">
                                <span className="text-sm font-medium" style={{ color: '#E8E8E8' }}>{idx + 1}. {agent.agent_name}</span>
                                <span className="text-xs" style={{ color: '#B0B0B0' }}>{agent.total_flights} flights</span>
                              </div>
                              <div className="flex justify-between text-xs mb-1" style={{ color: '#B0B0B0' }}>
                                <span>To Be Paid: &euro;{agent.total_to_be_paid.toFixed(2)}</span>
                                <span>Paid: &euro;{agent.total_paid.toFixed(2)}</span>
                              </div>
                              <div className="w-full bg-gray-700 rounded-full h-2 relative">
                                <div className="h-2 rounded-full absolute" style={{ width: `${percentage}%`, background: '#3B82F6', opacity: 0.3 }} />
                                <div className="h-2 rounded-full absolute" style={{ width: `${(percentage * paidPercentage) / 100}%`, background: '#22C55E' }} />
                              </div>
                              {agent.total_difference > 0 && (
                                <div className="text-xs mt-1" style={{ color: '#EF4444' }}>Outstanding: &euro;{agent.total_difference.toFixed(2)}</div>
                              )}
                            </div>
                          );
                        })}
                        {(costStats.agents || []).length === 0 && (
                          <div className="text-center py-4" style={{ color: '#B0B0B0' }}>No agent cost data available for the selected filters</div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  <CardHeader>
                    <CardTitle className="flex items-center" style={{ color: '#FFFFFF' }}>
                      <Calendar className="mr-2 h-5 w-5" /> Monthly Cost Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {costStatsLoading ? (
                      <div style={{ color: '#B0B0B0' }}>Loading...</div>
                    ) : (
                      <div className="space-y-3 max-h-96 overflow-auto">
                        {(costStats.monthly_breakdown || []).map((item) => {
                          const maxCost = Math.max(...(costStats.monthly_breakdown || []).map(m => m.to_be_paid));
                          const percentage = maxCost > 0 ? (item.to_be_paid / maxCost) * 100 : 0;
                          const paidPercentage = item.to_be_paid > 0 ? (item.paid / item.to_be_paid) * 100 : 0;
                          const [year, month] = item.month.split('-');
                          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                          const displayMonth = `${monthNames[parseInt(month) - 1]} ${year}`;
                          return (
                            <div key={item.month} className="p-3 rounded" style={{ background: '#0A1128' }}>
                              <div className="flex justify-between mb-2">
                                <span className="text-sm font-medium" style={{ color: '#E8E8E8' }}>{displayMonth}</span>
                                <span className="text-xs" style={{ color: '#B0B0B0' }}>{item.flights} flights</span>
                              </div>
                              <div className="flex justify-between text-xs mb-1" style={{ color: '#B0B0B0' }}>
                                <span>To Be Paid: &euro;{item.to_be_paid.toFixed(2)}</span>
                                <span>Paid: &euro;{item.paid.toFixed(2)}</span>
                              </div>
                              <div className="w-full bg-gray-700 rounded-full h-2 relative">
                                <div className="h-2 rounded-full absolute" style={{ width: `${percentage}%`, background: '#3B82F6', opacity: 0.3 }} />
                                <div className="h-2 rounded-full absolute" style={{ width: `${(percentage * paidPercentage) / 100}%`, background: '#22C55E' }} />
                              </div>
                              {item.difference > 0 && (
                                <div className="text-xs mt-1" style={{ color: '#EF4444' }}>Outstanding: &euro;{item.difference.toFixed(2)}</div>
                              )}
                            </div>
                          );
                        })}
                        {(costStats.monthly_breakdown || []).length === 0 && (
                          <div className="text-center py-4" style={{ color: '#B0B0B0' }}>No monthly cost data available for the selected filters</div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(open) => setDetailDialog({ ...detailDialog, open })}>
        <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A', maxWidth: '600px', maxHeight: '80vh', overflow: 'auto' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>
              {detailDialog.type === 'services' && 'All Services'}
              {detailDialog.type === 'airlines' && 'All Airlines'}
              {detailDialog.type === 'destinations' && 'All Destinations'}
              {detailDialog.type === 'revenue' && 'Revenue by Agency (All)'}
              {detailDialog.type === 'airports' && 'Flights by Airport (All)'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {detailDialog.data.map(([name, value], idx) => {
              const maxValue = Math.max(...detailDialog.data.map(([,v]) => v));
              const percentage = (value / maxValue) * 100;
              const color = detailDialog.type === 'services' ? '#22C55E' :
                           detailDialog.type === 'airlines' ? '#60A5FA' :
                           detailDialog.type === 'destinations' ? '#A855F7' :
                           detailDialog.type === 'revenue' ? '#D4AF37' : '#60A5FA';
              return (
                <div key={idx}>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm" style={{ color: '#E8E8E8' }}>{idx + 1}. {name}</span>
                    <span className="text-sm font-medium" style={{ color }}>
                      {detailDialog.type === 'revenue' ? `\u20AC${value.toFixed(2)}` : `${value}${detailDialog.type === 'services' ? 'x' : ' flights'}`}
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div className="h-2 rounded-full" style={{ width: `${percentage}%`, background: color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Statistics;
